import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, PackageSearch } from "lucide-react";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";

const categorySlugs = new Set(["new", "disposable", "pod-system", "open-system", "liquid"]);

function isRealProductSource(sourceUrl: string) {
  try {
    const url = new URL(sourceUrl);
    const segments = url.pathname.replace(/\/$/, "").split("/").filter(Boolean);
    return url.hostname.endsWith("igetvape.com") && segments[0] === "product" && segments.length === 2 && !categorySlugs.has(segments[1].toLowerCase());
  } catch {
    return false;
  }
}

function productSlug(sourceUrl: string) {
  const base = new URL(sourceUrl).pathname.split("/").filter(Boolean).pop()?.replace(/\.html?$/i, "") || "product";
  return base.startsWith("iget-") ? `iget-${base}` : base;
}

function cleanProductText(value: string | null | undefined) {
  return (value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanProductName(value: string) {
  return cleanProductText(value).replace(/\.html?$/i, "").trim();
}

type GridProduct = {
  sourceUrl: string;
  name: string;
  summary?: string | null;
  thumbnailUrl?: string;
  slug?: string;
};

const PAGE_SIZE = 12;

export default function PublishedProductGrid() {
  const { data: category, isLoading: isCategoryLoading } = trpc.products.getPublishedCategory.useQuery({ slug: "all-product" });
  const { data: publishedPages = [], isLoading: arePagesLoading } = trpc.products.getPublishedPages.useQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const categoryProducts = (category?.products ?? []).filter((product) => isRealProductSource(product.sourceUrl));
  const products: GridProduct[] = publishedPages.length
    ? publishedPages.map((page) => ({
        sourceUrl: page.sourceUrl,
        name: cleanProductName(page.name),
        summary: cleanProductText(page.description),
        thumbnailUrl: page.thumbnailUrl || page.imageUrls[0],
        slug: page.slug,
      }))
    : categoryProducts.map((product) => ({
        ...product,
        name: cleanProductName(product.name),
        summary: cleanProductText(product.summary),
      }));
  const isLoading = isCategoryLoading || arePagesLoading;
  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visibleProducts = products.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [category?.slug, publishedPages.length]);

  return (
    <main className="min-h-screen bg-white px-4 py-8 md:py-12">
      <div className="mx-auto max-w-7xl">
        <Link href="/">
          <Button variant="outline" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <header className="mb-8 border-b border-slate-200 pb-7">
          <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-100">IGET PRODUCTS</Badge>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-950 md:text-5xl">ALL PRODUCT</h1>
          <p className="mt-3 text-base text-slate-600">All copied IGET products. Select an image or product card to open its full product detail page.</p>
        </header>

        {isLoading ? (
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => <div key={item} className="h-72 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        ) : products.length === 0 ? (
          <Card className="flex flex-col items-center justify-center px-6 py-20 text-center">
            <PackageSearch className="h-12 w-12 text-slate-300" />
            <h2 className="mt-5 text-2xl font-semibold text-slate-900">No copied products yet</h2>
            <p className="mt-2 max-w-md text-slate-600">The product image grid will appear here after the administrator copies and publishes the ALL PRODUCT page.</p>
          </Card>
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between text-sm text-slate-500" aria-live="polite">
              <span>Page {safePage} of {totalPages} · {products.length} products</span>
              <span>{PAGE_SIZE} per page</span>
            </div>
            <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {visibleProducts.map((product, index) => {
              const slug = product.slug || productSlug(product.sourceUrl);
              const imageLoading = index < 4 ? "eager" : "lazy";
              return (
                <Link key={`${product.sourceUrl}-${slug}`} href={`/product-pages/${slug}`}>
                  <Card className="group h-full cursor-pointer overflow-hidden border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-1">
                    <div className="flex aspect-[4/3] min-h-48 items-center justify-center overflow-hidden rounded-xl bg-slate-50 p-3">
                      {product.thumbnailUrl ? (
                        <img src={product.thumbnailUrl} alt={product.name} loading={imageLoading} decoding="async" className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="text-sm text-slate-400">No product thumbnail</div>
                      )}
                    </div>
                    <div className="px-2 pt-3">
                      <h2 className="line-clamp-2 text-lg font-semibold leading-6 text-slate-900">{product.name}</h2>
                      {product.summary && <p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-500">{product.summary}</p>}
                      <span className="mt-3 inline-flex items-center text-sm font-medium text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">
                        View product details <ArrowRight className="ml-1 h-4 w-4" />
                      </span>
                    </div>
                  </Card>
                </Link>
              );
            })}
            </div>
            <nav className="mt-12 flex flex-wrap items-center justify-center gap-2" aria-label="Product pagination">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={safePage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <Button
                  key={page}
                  type="button"
                  variant={page === safePage ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  aria-current={page === safePage ? "page" : undefined}
                  aria-label={`Page ${page}`}
                >
                  {page}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={safePage === totalPages}
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                aria-label="Next page"
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </nav>
          </>
        )}
      </div>
    </main>
  );
}

